import React, { useState, useEffect } from 'react';
import { businessAPI } from '../../api/businesses';
import { bookingAPI } from '../../api/bookings';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import { getLocalTimeHMFromUtc } from '../../utils/dateUtils';
import { resolveImageUrl } from '../../utils/formatters';

const StaffSchedule = () => {
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    loadStaff();
  }, []);

  useEffect(() => {
    if (selectedStaff) {
      loadSchedule();
    }
  }, [selectedStaff, currentWeek]);

  const loadStaff = async () => {
    try {
      const response = await businessAPI.getStaff();
      setStaff(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedStaff(response.data.data[0]);
      }
    } catch (error) {
      console.error('Failed to load staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    try {
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekDays = [];
      for (let i = 0; i < 7; i++) {
        const date = addDays(weekStart, i);
        weekDays.push(format(date, 'yyyy-MM-dd'));
      }

      // Fetch bookings for each day
      const scheduleData = [];
      for (const date of weekDays) {
        const response = await bookingAPI.getBookings({
          staff_id: selectedStaff.id,
          date: date
        });
        scheduleData.push({
          date,
          bookings: response.data.data
        });
      }
      setSchedule(scheduleData);
    } catch (error) {
      console.error('Failed to load schedule:', error);
    }
  };

  const handlePreviousWeek = () => {
    setCurrentWeek(addDays(currentWeek, -7));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addDays(currentWeek, 7));
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Staff Schedule</h1>
          <p className="text-gray-600">View and manage staff appointments</p>
        </div>
        <select
          value={selectedStaff?.id || ''}
          onChange={(e) => {
            const staffMember = staff.find(s => s.id === parseInt(e.target.value));
            setSelectedStaff(staffMember);
            setAvatarError(false);
          }}
          className="input w-64"
        >
          {staff.map((member) => (
            <option key={member.id} value={member.id}>
              {member.first_name} {member.last_name} - {member.position}
            </option>
          ))}
        </select>
      </div>

      {selectedStaff && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 border-b flex items-center gap-3">
            {selectedStaff.profile_image && !avatarError ? (
              <img
                src={resolveImageUrl(selectedStaff.profile_image)}
                alt={`${selectedStaff.first_name || ''} ${selectedStaff.last_name || ''}`.trim() || 'Staff'}
                className="h-10 w-10 rounded-full object-cover border"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div className="h-10 w-10 rounded-full border bg-gray-50 flex items-center justify-center text-sm font-bold text-gray-500">
                {(selectedStaff.first_name || 'S').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate">{selectedStaff.first_name} {selectedStaff.last_name}</div>
              <div className="text-sm text-gray-500 truncate">{selectedStaff.position}</div>
            </div>
          </div>
          <div className="p-4 border-b flex justify-between items-center">
            <button
              onClick={handlePreviousWeek}
              className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
            >
              Previous Week
            </button>
            <h2 className="text-lg font-semibold">
              Week of {format(currentWeek, 'MMM dd, yyyy')}
            </h2>
            <button
              onClick={handleNextWeek}
              className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
            >
              Next Week
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  {schedule.map((day) => (
                    <th key={day.date} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {format(new Date(`${day.date}T00:00:00`), 'EEE, MMM dd')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.from({ length: 12 }, (_, i) => {
                  const hour = 9 + i;
                  const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
                  return (
                    <tr key={hour}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{timeSlot}</td>
                      {schedule.map((day) => {
                        const booking = day.bookings.find(b => getLocalTimeHMFromUtc(b.start_time, day.date) === timeSlot);
                        return (
                          <td key={day.date} className="px-4 py-3">
                            {booking ? (
                              <div className="bg-blue-100 p-2 rounded text-sm">
                                <p className="font-medium">{booking.service_name}</p>
                                <p className="text-xs text-gray-600">{booking.customer_name}</p>
                              </div>
                            ) : (
                              <div className="h-12 border-2 border-dashed border-gray-200 rounded"></div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffSchedule;
